import { useState, useEffect } from "react";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  DollarSign,
  Eye,
  ShoppingCart,
  CreditCard,
  Type,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Loader2,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAnalytics() {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState(30);
  
  // Headlines state
  const [headlines, setHeadlines] = useState([]);
  const [listeners, setListeners] = useState([]);
  const [savingHeadlines, setSavingHeadlines] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchHeadlines();
    fetchListeners();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics/overview?days=${period}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Error al cargar analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchHeadlines = async () => {
    try {
      const response = await axios.get(`${API}/admin/headlines`);
      setHeadlines(response.data.headlines || []);
    } catch (error) {
      console.error("Error fetching headlines:", error);
    }
  };

  const fetchListeners = async () => {
    try {
      const response = await axios.get(`${API}/admin/listeners`);
      setListeners(response.data || []);
    } catch (error) {
      console.error("Error fetching listeners:", error);
    }
  };

  const addHeadline = () => {
    setHeadlines([
      ...headlines,
      { id: `h${Date.now()}`, text: "", active: true, listener_id: null }
    ]);
  };

  const updateHeadline = (index, field, value) => {
    const updated = [...headlines];
    updated[index][field] = value;
    setHeadlines(updated);
  };

  const removeHeadline = (index) => {
    setHeadlines(headlines.filter((_, i) => i !== index));
  };

  const saveHeadlines = async () => {
    setSavingHeadlines(true);
    try {
      await axios.put(`${API}/admin/headlines`, { headlines });
      toast.success("Headlines guardados");
    } catch (error) {
      console.error("Error saving headlines:", error);
      toast.error("Error al guardar headlines");
    } finally {
      setSavingHeadlines(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "primary" }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-stone-500">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-stone-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl bg-${color}/10`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-3 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span>{Math.abs(trend)}% vs periodo anterior</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <AdminLayout title="Analytics & Headlines">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Estadísticas
          </TabsTrigger>
          <TabsTrigger value="headlines" className="flex items-center gap-2">
            <Type className="w-4 h-4" />
            Headlines
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Period Selector */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label>Periodo:</Label>
              <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Últimos 7 días</SelectItem>
                  <SelectItem value="30">Últimos 30 días</SelectItem>
                  <SelectItem value="90">Últimos 90 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchAnalytics} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : analytics ? (
            <>
              {/* Main Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Vistas de Perfil"
                  value={analytics.overview.total_profile_views}
                  icon={Eye}
                  color="blue-500"
                />
                <StatCard
                  title="Checkouts Iniciados"
                  value={analytics.overview.total_checkouts_started}
                  icon={ShoppingCart}
                  color="amber-500"
                />
                <StatCard
                  title="Compras"
                  value={analytics.overview.total_purchases}
                  icon={CreditCard}
                  color="green-500"
                />
                <StatCard
                  title="Revenue USD"
                  value={`$${analytics.overview.total_revenue_usd}`}
                  subtitle={analytics.overview.total_revenue_mxn > 0 ? `+ $${analytics.overview.total_revenue_mxn} MXN` : null}
                  icon={DollarSign}
                  color="primary"
                />
              </div>

              {/* Conversion Rates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Conversión: Perfil → Checkout</CardTitle>
                    <CardDescription>Porcentaje de visitantes que inician checkout</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-bold text-primary">
                        {analytics.conversion_rates.profile_to_checkout}%
                      </span>
                    </div>
                    <div className="mt-4 bg-stone-100 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-primary h-full rounded-full transition-all"
                        style={{ width: `${Math.min(analytics.conversion_rates.profile_to_checkout, 100)}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Conversión: Checkout → Compra</CardTitle>
                    <CardDescription>Porcentaje que completa la compra</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-bold text-green-600">
                        {analytics.conversion_rates.checkout_to_purchase}%
                      </span>
                    </div>
                    <div className="mt-4 bg-stone-100 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-green-500 h-full rounded-full transition-all"
                        style={{ width: `${Math.min(analytics.conversion_rates.checkout_to_purchase, 100)}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* By Listener */}
              {analytics.by_listener && analytics.by_listener.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Por Listener</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-2 text-sm font-medium text-stone-500">Listener</th>
                            <th className="text-right py-3 px-2 text-sm font-medium text-stone-500">Vistas</th>
                            <th className="text-right py-3 px-2 text-sm font-medium text-stone-500">Checkouts</th>
                            <th className="text-right py-3 px-2 text-sm font-medium text-stone-500">Compras</th>
                            <th className="text-right py-3 px-2 text-sm font-medium text-stone-500">Conv.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.by_listener.map((listener, i) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="py-3 px-2">
                                <p className="font-medium">{listener.name}</p>
                                <p className="text-xs text-stone-400">@{listener.username}</p>
                              </td>
                              <td className="text-right py-3 px-2">{listener.views}</td>
                              <td className="text-right py-3 px-2">{listener.checkouts}</td>
                              <td className="text-right py-3 px-2 font-medium text-green-600">{listener.purchases}</td>
                              <td className="text-right py-3 px-2">
                                {listener.views > 0 
                                  ? `${((listener.purchases / listener.views) * 100).toFixed(1)}%`
                                  : '-'
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Daily Chart Placeholder */}
              {analytics.daily && analytics.daily.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Últimos 7 días</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-7 gap-2">
                      {analytics.daily.map((day, i) => (
                        <div key={i} className="text-center">
                          <div className="text-xs text-stone-400 mb-2">
                            {new Date(day.date).toLocaleDateString('es', { weekday: 'short' })}
                          </div>
                          <div className="bg-stone-100 rounded-lg p-2">
                            <p className="text-lg font-bold text-primary">{day.views}</p>
                            <p className="text-xs text-stone-500">vistas</p>
                          </div>
                          <div className="mt-1 text-xs">
                            <span className="text-green-600">{day.purchases}</span> ventas
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No hay datos de analytics disponibles</p>
                <p className="text-sm text-stone-400 mt-1">Activa el tracking para empezar a recolectar datos</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Headlines Tab */}
        <TabsContent value="headlines" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Headlines para A/B Testing</CardTitle>
              <CardDescription>
                Configura los encabezados que se mostrarán en los perfiles. Se elige uno al azar para cada visitante.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {headlines.map((headline, index) => (
                <div key={headline.id} className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg">
                  <div className="flex-1 space-y-3">
                    <div>
                      <Label>Texto del headline</Label>
                      <Input
                        value={headline.text}
                        onChange={(e) => updateHeadline(index, "text", e.target.value)}
                        placeholder="Ej: Habla con alguien que te escuche hoy mismo"
                        className="mt-1"
                      />
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={headline.active}
                          onCheckedChange={(checked) => updateHeadline(index, "active", checked)}
                        />
                        <Label className="text-sm">Activo</Label>
                      </div>
                      <div className="flex-1">
                        <Select 
                          value={headline.listener_id || "global"} 
                          onValueChange={(v) => updateHeadline(index, "listener_id", v === "global" ? null : v)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Aplica a..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="global">Todos los listeners</SelectItem>
                            {listeners.map((l) => (
                              <SelectItem key={l.id} value={l.id}>
                                Solo {l.name} (@{l.username})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeHeadline(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              <Button variant="outline" onClick={addHeadline} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Agregar headline
              </Button>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveHeadlines} disabled={savingHeadlines}>
                  {savingHeadlines ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar Headlines
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tips para Headlines</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-stone-600">
                <li>✓ Usa lenguaje emocional: "¿Te sientes solo?" en vez de "Servicio de escucha"</li>
                <li>✓ Habla directo al usuario: "Habla con alguien" en vez de "Ofrecemos conversaciones"</li>
                <li>✓ Evita palabras clínicas: "conversación" en vez de "sesión terapéutica"</li>
                <li>✓ Crea urgencia suave: "hoy mismo", "ahora"</li>
                <li>✓ Prueba variantes y revisa las estadísticas</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
